import { EditableText } from '~/components/EditableText'
import { WorkspaceObject } from '~/features/workspaces/types'
import { useUpdateWorkspace, useWorkspaceMembers, useWorkspaces } from '~/hooks'
import { Button, Label, Card, CardContent, CardFooter, showErrorToast, showSuccessToast } from '@bpinternal/ui-kit-next'
import { wasResolved } from '~/shared/helpers'
import { LeaveWorkspaceButton } from './LeaveWorkspace'
import { useMemo, useState } from 'react'
import { useCheckWorkspaceHandleAvailability } from '~/hooks/workspaces/useCheckWorkspaceHandle'
import _ from 'lodash'
import { WorkspaceHandleMessage, makeHandleUrl } from './WorkspaceHandleMessage'
import { Permission } from '~/features/users/types'
import { trackEvent } from '~/providers'
import { copyToClipboard } from '~/utils'
import { Copy } from 'lucide-react'
import { WorkspaceProfilePicture } from '~/features/workspaces/components'
import { ProfilePictureField } from '~/components/ProfilePictureField'

type Props = {
  workspace: WorkspaceObject
  isAuthorized: (permission: Permission) => boolean | undefined
  navigateToHome: () => Promise<void>
}

const sanitizeHandle = (handle: string) => handle.toLowerCase().replace(/[^a-z0-9-_]/gi, '')

const CONFIRM_HANDLE_MESSAGE =
  'Are you sure you want to change your workspace handle?\n\nMake sure there are no external links pointing to the old handle, it will not be reachable anymore and will be available for others to claim.'

export const WorkspaceDetails = ({ workspace, isAuthorized, navigateToHome }: Props) => {
  const [handleErrorMsg, setHandleError] = useState<string | null>(null)
  const [handleAvailable, setHandleAvailable] = useState<boolean | null>(null)
  const [profilePicture, setProfilePicture] = useState<string | undefined>(workspace.profilePicture)

  const { data: members } = useWorkspaceMembers({ workspaceId: workspace.id })
  const { data: workspaces } = useWorkspaces()
  const { mutateAsync: updateWorkspace } = useUpdateWorkspace()
  const { mutateAsync: checkHandleAvailability } = useCheckWorkspaceHandleAvailability()

  const saveWorkspaceName = async (newName: string) => {
    if (workspaces?.some((x) => x.id !== workspace.id && x.name === newName)) {
      showErrorToast(`Sorry, you already have a workspace named "${newName}", please choose a different name.`)
      return false
    }

    return await wasResolved(
      updateWorkspace({
        id: workspace.id,
        name: newName.trim(),
        onSuccess: () => {
          showSuccessToast(`The workspace has been renamed to "${newName.trim()}".`)
        },
      })
    )
  }

  const updateHandle = async (handle: string) => {
    handle = sanitizeHandle(handle)
    return updateWorkspace(
      { id: workspace.id, handle },
      {
        onSuccess: () => {
          trackEvent({ type: 'update_handle', workspaceId: workspace.id })
          showSuccessToast(
            <span className="meticulous-ignore">
              The workspace handle has been updated. You can now access this workspace by navigating to{' '}
              {makeHandleUrl(handle).forHumans}
            </span>
          )
        },
        onError: (error) => {
          showErrorToast(`Failed to update the workspace handle: ${error.message}`)
          throw error
        },
      }
    )
  }

  const confirmUpdateHandle = async (handle: string): Promise<boolean> => {
    // TODO remplace the confirm with a cute modal
    if (!handle || handle === workspace.handle) {
      return false
    }

    if (!workspace.handle || window.confirm(CONFIRM_HANDLE_MESSAGE)) {
      return updateHandle(handle)
        .then(() => {
          setHandleError(null)
          setHandleAvailable(null)
          return true
        })
        .catch(() => false)
    }
    return false
  }

  const updateProfilePicture = async (profilePicture: string) => {
    setProfilePicture(profilePicture)

    return updateWorkspace(
      { id: workspace.id, profilePicture },
      {
        onSuccess: () => {
          showSuccessToast('The workspace profile picture has been updated.')
        },
      }
    )
  }

  const debouncedValidate = useMemo(() => {
    return _.debounce(
      async (candidate: string) => {
        candidate = sanitizeHandle(candidate)
        if (!/^[a-z][a-zA-Z0-9_-]+[a-zA-Z0-9]+$/i.test(candidate)) {
          setHandleError('Invalid handle format. Only letters, numbers, dashes and underscores are allowed.')
          return false
        }

        const avalability = await checkHandleAvailability({ handle: candidate })
        if (!avalability.available) {
          setHandleError(
            `Handle is not available.${
              avalability.suggestions.length ? ` Try one of these: ${avalability.suggestions.join(', ')}` : ''
            }`
          )
        } else {
          setHandleError(null)
        }

        setHandleAvailable(avalability.available)
        return avalability.available
      },
      300,
      { trailing: true }
    )
  }, [])

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <h3 className="pb-6 text-lg">Workspace Details</h3>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Label htmlFor="display-name" className="text-muted-foreground">
              Display Name
            </Label>
            <EditableText
              value={workspace.name}
              onSave={saveWorkspaceName}
              validate={(name) => name.length >= 3 && name.length <= 100}
              readonly={!isAuthorized('workspace.update')}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="handle" className="text-muted-foreground">
              Handle
            </Label>
            <EditableText
              value={workspace.handle ?? ''}
              onSave={confirmUpdateHandle}
              onChange={(handleValue) => {
                if (!handleValue) {
                  setHandleError('Handle cannot be empty')
                } else {
                  void debouncedValidate(handleValue)
                }
              }}
              readonly={!isAuthorized('workspace.update')}
              emptyButtonText="Set Workspace Handle"
              isValid={handleErrorMsg == null}
              onCancel={() => {
                setHandleError(null)
                setHandleAvailable(null)
              }}
            />
            <WorkspaceHandleMessage
              workspaceHandle={workspace.handle}
              errorMessage={handleErrorMsg}
              successMessage={handleAvailable ? 'Handle is available' : null}
            />
          </div>
          <div>
            <Label className="text-muted-foreground">Workspace Owner</Label>
            <p className="text-sm">{members?.find((x) => x.userId === workspace.ownerId)?.email}</p>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-muted-foreground">Workspace ID</Label>
            <Button
              variant="ghost"
              size="default"
              onClick={() => copyToClipboard(workspace.id, 'workspace ID')}
              className="-ml-1 flex h-6 w-fit justify-start p-1 font-normal"
            >
              {workspace.id}
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <ProfilePictureField onChange={(profilePicture) => updateProfilePicture(profilePicture)}>
            <WorkspaceProfilePicture
              pictureUrl={profilePicture}
              workspaceId={workspace.id}
              className="mb-1 h-44 w-44"
            />
          </ProfilePictureField>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end p-6 pt-0">
        <LeaveWorkspaceButton workspace={workspace} navigateToHome={navigateToHome} />
      </CardFooter>
    </Card>
  )
}
